import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { UNIT_OPTIONS } from '../../utils/kitchenTasks';

const parseQty = (v) => parseFloat(String(v ?? '').replace(',', '.').trim());

const CompleteTaskDialog = ({ task, open, onOpenChange, onConfirm, busy }) => {
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (task) { setQty(task.planned_quantity != null ? String(task.planned_quantity) : ''); setUnit(task.unit || ''); setComment(''); }
  }, [task]);

  if (!task) return null;
  const parsed = parseQty(qty);
  const valid = qty.trim() !== '' && !Number.isNaN(parsed) && parsed >= 0;
  const units = unit && !UNIT_OPTIONS.includes(unit) ? [unit, ...UNIT_OPTIONS] : UNIT_OPTIONS;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="bg-white border-gray-200 max-w-md" data-testid="complete-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-gray-900">Terminer la tâche : {task.title}</DialogTitle>
          <DialogDescription className="text-gray-500">Indiquez la quantité réellement produite, puis confirmez : la tâche passera directement au statut « Terminée ».</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="actual-quantity" className="text-gray-700">Quantité produite *</Label>
              <Input id="actual-quantity" type="text" inputMode="decimal" autoComplete="off" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 text-lg" data-testid="actual-quantity-input" />
            </div>
            <div>
              <Label htmlFor="actual-unit" className="text-gray-700">Unité</Label>
              <select id="actual-unit" value={unit} onChange={(e) => setUnit(e.target.value)} className="mt-1 block w-full h-12 px-3 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-neon-pink/50" data-testid="actual-unit-select">
                <option value="">—</option>
                {units.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          {task.planned_quantity != null && <p className="text-xs text-gray-500">Quantité demandée : {task.planned_quantity} {task.unit}</p>}
          <div>
            <Label htmlFor="collaborator-comment" className="text-gray-700">Commentaire (facultatif)</Label>
            <Textarea id="collaborator-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Remarque pour le chef…" className="mt-1 rounded-xl bg-gray-100 border-gray-200 text-gray-900" data-testid="collaborator-comment-input" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="flex-1 h-12 rounded-full border-gray-300" data-testid="complete-cancel-btn">Annuler</Button>
          <Button onClick={() => onConfirm({ actual_quantity: parsed, unit, collaborator_comment: comment })} disabled={busy || !valid} className="flex-1 h-12 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="complete-confirm-btn">
            {busy ? <Loader2 size={18} className="animate-spin mr-2" /> : <CheckCircle2 size={18} className="mr-2" />}Terminer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteTaskDialog;
